import type { FindDeliveryByIdQuery } from "@/libs/generated/operations";

type Delivery = FindDeliveryByIdQuery["findDeliveryById"];

import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import { Button, Divider, Grid, Stack, TextField, Typography } from "@mui/material";
import { formatLongDate, formatLongDateTime, formatTime, toDate } from "@repo/utils/date";
import type { DeliveryType } from "@types_def/models/delivery.types";
import { DeliveryStatus } from "@types_def/models/delivery.types";
import { formatRoute } from "@utils/functions/formatRoute";
import type { Params } from "react-router-dom";
import { useNavigate, useParams } from "react-router-dom";

import { useGetSmsLink, useSendSMS } from "@/features/delivery/hooks/use-delivery-actions";

const MINUTE_MS = 60_000;

type TSMSStatus = {
  data: Delivery;
};

function SMSStatus({ data }: TSMSStatus) {
  const smsSentAt = data.smsSentAt;
  const smsConfirmedAt = data.confirmedByCustomerAt;
  const smsConfirmedAtColor = smsConfirmedAt ? "success" : "error";
  const smsSentAtColor = smsSentAt ? "success" : "error";
  const smsSentAtIcon = smsSentAt ? (
    <CheckCircleOutlineRoundedIcon color={smsSentAtColor} fontSize="small" />
  ) : (
    <HighlightOffRoundedIcon color={smsSentAtColor} fontSize="small" />
  );
  const smsConfirmedAtIcon = smsConfirmedAt ? (
    <CheckCircleOutlineRoundedIcon color={smsConfirmedAtColor} fontSize="small" />
  ) : (
    <HighlightOffRoundedIcon color={smsConfirmedAtColor} fontSize="small" />
  );

  const smsSentAtText = smsSentAt
    ? `SMS envoyé le ${formatLongDateTime(smsSentAt)}`
    : "SMS Non envoyé";

  const smsConfirmedAtText = smsConfirmedAt
    ? `SMS confirmé le ${formatLongDateTime(smsConfirmedAt)}`
    : "SMS Non confirmé";

  return (
    <Stack direction="column" spacing={1}>
      <Stack direction="row" spacing={1}>
        {smsSentAtIcon}
        <Typography variant="body1">{smsSentAtText}</Typography>
      </Stack>
      <Stack direction="row" spacing={1}>
        {smsConfirmedAtIcon}
        <Typography variant="body1">{smsConfirmedAtText}</Typography>
      </Stack>
    </Stack>
  );
}

type TSMSForm = {
  data: Delivery;
  isRestitution?: boolean;
};

function SMSForm({ data, isRestitution = false }: TSMSForm) {
  const { data: smsLink } = useGetSmsLink(data.id);
  const { type, status } = useParams<Params>();
  const { mutate: sendSMS } = useSendSMS();
  const navigation = useNavigate();

  const onSendSMS = () => {
    sendSMS(
      { id: data.id, message, clientPhone: data.clientPhone },
      {
        onSuccess: () => {
          navigation(
            `/admin/pilot/${formatRoute(type as DeliveryType)}/pending-customer-confirmation/${data.id}`
          );
        },
      }
    );
  };

  if (!data.carPickUpDate || !data.toAddress || !data.vehicles[0]) {
    return (
      <Typography>Les informations de livraison sont incomplètes pour envoyer un SMS.</Typography>
    );
  }
  const deliveryTime = toDate(data.carPickUpDate)?.getTime() ?? Number.NaN;
  const deliveryDatePlus80 = deliveryTime + 80 * MINUTE_MS;
  const deliveryDatePlus110 = deliveryTime + 110 * MINUTE_MS;

  const formattedDateTime = `${formatLongDate(deliveryDatePlus80)} entre ${formatTime(deliveryDatePlus80)} et ${formatTime(deliveryDatePlus110)}`;

  const commun = `Bonjour, nous avons été mandatés par la société ${data.representativeCompany} \
  pour effectuer la livraison de votre véhicule : ${data.vehicles[0].marque} ${data.vehicles[0].model} (immatriculation : ${data.vehicles[0].vehicleCode}).\
  La livraison est prévue le ${formattedDateTime} à l'adresse suivante : ${data.toAddress.address} ${data.toAddress.postalCode}, ${data.toAddress.city}.`;

  const restitution = `Et aussi pour effectuer la restitution de votre véhicule : ${data.vehicles[1]?.marque} ${data.vehicles[1]?.model} (immatriculation : ${data.vehicles[1]?.vehicleCode}).
  à l'adresse suivante : ${data?.toReturnToAddress?.address} ${data?.toReturnToAddress?.postalCode}, ${data?.toReturnToAddress?.city}.`;

  const end = `\nVeuillez confirmer votre présence en cliquant sur le lien : ${smsLink}`;

  const message = isRestitution ? commun + restitution + end : commun + end;

  return (
    <Grid container spacing={2} paddingBottom={2}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="h5">Envoi d'un SMS </Typography>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Divider sx={{ bgcolor: "#fe5d8d" }} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField type="text" label="SMS Message" fullWidth disabled multiline value={message} />
        <Stack direction="column" spacing={1} marginTop={2}>
          <SMSStatus data={data} />
          <Button
            variant="contained"
            color="primary"
            sx={{ width: "200px" }}
            disabled={Boolean(data.smsSentAt) || status?.toUpperCase() !== DeliveryStatus.REQUESTED}
            onClick={onSendSMS}
          >
            Envoyer le SMS
          </Button>
        </Stack>
      </Grid>
    </Grid>
  );
}

export default SMSForm;
